import React, { Component } from "react";
import PropTypes from "prop-types";
import EnergyChartGraph from "./EnergyChartGraph";
import EnergyChartHeader from "./EnergyChartHeader";
import EnergyChartFooter from "./EnergyChartFooter";
import {
  getDateRangeFromDateAndWindow,
  getNextWindow,
  getPreviousWindow,
  isDateAfterEndDate,
  isKnownWindow
} from "./windows";
import { TimeRange, TimeSeries } from "pondjs";
import moment from "moment";
import getResolutionIntervalMinutes from "../../model/sensors/getResolutionIntervalMinutes";
import fillDatesArray from "../../utils/fillDatesArray";

const ignoredAlertTypes = ["Disconnected"];
const dateTimeFormat = "YYYY-MM-DD HH:mm:ss";

class EnergyChart extends Component {
  constructor(props) {
    super(props);

    const { from, to } = isKnownWindow(props.window)
      ? getDateRangeFromDateAndWindow(props.date || new Date(), props.window)
      : props.getDateRangeForWindow(props.window);

    this.state.from = from;
    this.state.to = to;
  }

  state = {
    window: this.props.window,
    isLoading: true,
    loadingFailed: false,
    showStoreClosedCheckbox: false,
    showStoreClosedOverlay: false,
    closedTimes: [],
    lightsOverThresholdAlertThresholds: [],
    hvacOverThresholdAlertThresholds: []
  };

  componentDidMount() {
    this.loadData(this.state.from, this.state.to);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sensorId !== this.props.sensorId) {
      this.loadData(this.state.from, this.state.to);
    }
  }

  createPointsWithGaps(points) {
    const dataResolution =
      this.props.dataResolution ||
      this.getResolutionFromDateRange(this.state.from, this.state.to);

    const toleranceWindowMilliseconds =
      getResolutionIntervalMinutes(dataResolution) * 2 * 60 * 1000;

    const from = this.state.loadedDataFrom || this.state.from;
    const to = this.state.loadedDataTo || this.state.to;

    const pointsInRange = points.filter(p => p[0] >= from && p[0] <= to);

    const newPoints = [];
    for (let i = 0; i < pointsInRange.length; i++) {
      const p = pointsInRange[i];

      if (i !== 0 && p[1] !== null) {
        const lastNewPoint = newPoints[newPoints.length - 1];
        if (lastNewPoint[1] !== null) {
          // If any point is greater than 2 * <dataResolution> after the previous point insert a null value
          // (which produces a gap on the graph).
          const lastPoint = pointsInRange[i - 1];
          const thisPointTime = p[0];
          const lastPointTime = lastPoint[0];
          const diffBetweenThisAndLastPointMilliseconds = Math.abs(
            thisPointTime - lastPointTime
          );

          if (
            diffBetweenThisAndLastPointMilliseconds >=
            toleranceWindowMilliseconds
          ) {
            // The time for the dummy point is unimportant as long as it's between thisPoint and lastPoint
            const insertedTime = new moment(lastPointTime)
              .add(10, "seconds")
              .toDate();

            newPoints.push([insertedTime, null]);
          }
        }
      }

      newPoints.push(p);
    }

    return newPoints;
  }

  createActualsSeries() {
    const points = this.createPointsWithGaps(
      this.state.values.map(v => [new Date(v[0]), parseFloat(v[1])])
    );

    return new TimeSeries({
      name: "actual",
      columns: ["time", "actual"],
      points: points
    });
  }

  createBaselineSeries() {
    const points = this.createPointsWithGaps(
      this.state.values.map(v => [new Date(v[0]), parseFloat(v[2])])
    );

    return new TimeSeries({
      name: "baseline",
      columns: ["time", "baseline"],
      points: points
    });
  }

  createEmptySeries(name, valueColumnName) {
    return new TimeSeries({
      name: name,
      columns: ["time", valueColumnName],
      points: []
    });
  }

  createActualAlertOverlaySeries() {
    const pointsInsideAlertEvents = [];

    const alertWindows = this.mergeAlertEventWindows();

    // N.B. We round the times to the nearest second here before comparing
    // as we see slight differences in the times an Alert Event is Started
    // and a Sensor Data point's timestamp.

    for (let i = 0, a = 0; i < this.state.values.length; i++) {
      const point = this.state.values[i];
      const pointTime = new Date(point[0]);
      const pointTimeSecond = new moment(pointTime).startOf("second").toDate();

      let insideAlertEvent = false;

      // Is this point inside an Alert Event?
      for (; a < alertWindows.length; a++) {
        const alertEvent = alertWindows[a];

        const alertEventStarted = new Date(alertEvent.started);
        const alertEventStartedSecond = new moment(alertEventStarted)
          .startOf("second")
          .toDate();
        const alertEventEndedSecond = alertEvent.ended
          ? new moment(alertEvent.ended).startOf("second").toDate()
          : null;

        if (
          alertEventStartedSecond <= pointTimeSecond &&
          (!alertEvent.ended || alertEventEndedSecond >= pointTimeSecond)
        ) {
          insideAlertEvent = true;
          break;
        } else if (alertEventStarted > pointTime) {
          // Stop looking - we've gone past the times for this point
          break;
        }
      }

      if (insideAlertEvent) {
        pointsInsideAlertEvents.push([pointTime, parseFloat(point[1])]);
      } else if (
        pointsInsideAlertEvents.length &&
        pointsInsideAlertEvents[pointsInsideAlertEvents.length - 1][1] !== null
      ) {
        pointsInsideAlertEvents.push([pointTime, null]);
      }
    }

    return new TimeSeries({
      name: "actualAlertOverlay",
      columns: ["time", "alertActual"],
      points: pointsInsideAlertEvents
    });
  }

  createAlertSeries() {
    const alertWindows = this.mergeAlertEventWindows();

    const effectiveTo = this.getEffectiveTo();

    const points = alertWindows.reduce((xs, x) => {
      const newPoints = [...xs];

      newPoints.push([new Date(x.started), 0]);

      if (x.ended) {
        newPoints.push([new Date(x.ended), 0]);

        newPoints.push([new moment(x.ended).add(1, "seconds").toDate(), null]);
      } else {
        newPoints.push([effectiveTo, 0]);
      }

      return newPoints;
    }, []);

    // Points must be in chronological order
    points.sort((a, b) => {
      if (a[0] === b[0]) {
        return 0;
      }

      return a[0] < b[0] ? -1 : 1;
    });

    return new TimeSeries({
      name: "alert",
      columns: ["time", "zeroAlertMarker"],
      points: points
    });
  }

  createLightsOverThresholdAlertSeries() {
    const points = this.createPointsWithGaps(
      this.state.lightsOverThresholdAlertThresholds.reduce(
        (thresholds, currentThreshold) => {
          // Look up the closed period for this threshold

          const thresholdTime = moment.utc(currentThreshold.time);

          const closedPeriods = this.state.closedTimes.filter(closedPeriod =>
            thresholdTime.isBetween(
              moment(closedPeriod.begin())
                .utc()
                .subtract(60, "minutes"),
              moment(closedPeriod.begin())
                .utc()
                .add(60, "minutes"),
              null,
              "[]"
            )
          );

          if (closedPeriods.length === 0) {
            return thresholds;
          }

          const closedPeriod = closedPeriods.slice(-1)[0];

          // Extrapolate across this closed period

          const resolution = this.getResolutionFromDateRange(
            closedPeriod.begin(),
            closedPeriod.end()
          );

          const resolutionInterval = getResolutionIntervalMinutes(resolution);
          const thresholdsForClosedPeriod = fillDatesArray(
            closedPeriod,
            resolutionInterval,
            "minutes"
          ).map(date => [date, parseFloat(currentThreshold.value)]);

          return thresholds.concat(thresholdsForClosedPeriod);
        },
        []
      )
    );

    return new TimeSeries({
      name: "lightsOverThresholdAlertThreshold",
      columns: ["time", "lightsOverThresholdAlertThreshold"],
      points: points
    });
  }

  createHvacOverThresholdAlertSeries() {
    const points = this.createPointsWithGaps(
      this.state.hvacOverThresholdAlertThresholds.reduce(
        (thresholds, currentThreshold) => {
          // Look up the closed period for this threshold

          const thresholdTime = moment.utc(currentThreshold.time);

          const closedPeriods = this.state.closedTimes.filter(closedPeriod =>
            thresholdTime.isBetween(
              moment(closedPeriod.begin())
                .utc()
                .subtract(60, "minutes"),
              moment(closedPeriod.begin())
                .utc()
                .add(60, "minutes"),
              null,
              "[]"
            )
          );

          if (closedPeriods.length === 0) {
            return thresholds;
          }

          const closedPeriod = closedPeriods.slice(-1)[0];

          // Extrapolate across this closed period

          const resolution = this.getResolutionFromDateRange(
            closedPeriod.begin(),
            closedPeriod.end()
          );

          const resolutionInterval = getResolutionIntervalMinutes(resolution);
          const thresholdsForClosedPeriod = fillDatesArray(
            closedPeriod,
            resolutionInterval,
            "minutes"
          ).map(date => [date, parseFloat(currentThreshold.value)]);

          return thresholds.concat(thresholdsForClosedPeriod);
        },
        []
      )
    );

    return new TimeSeries({
      name: "hvacOverThresholdAlertThreshold",
      columns: ["time", "hvacOverThresholdAlertThreshold"],
      points: points
    });
  }

  mergeAlertEventWindows() {
    return this.state.alertEvents
      .filter(ae => !ignoredAlertTypes.includes(ae.alertType))
      .map(ae => ({
        started: ae.started,
        ended: ae.ended
      }))
      .sort((e1, e2) => moment.utc(e1.started) - moment.utc(e2.started))
      .reduce((xs, x, i) => {
        if (i === 0) {
          return [x];
        }

        // If this event started before the last event ended, merge them
        const previousEvent = xs[xs.length - 1];
        if (x.started <= previousEvent.ended || !previousEvent.ended) {
          const ended =
            previousEvent.ended === null || x.ended === null
              ? null
              : x.ended > previousEvent.ended
              ? x.ended
              : previousEvent.ended;

          return [
            ...xs.slice(0, xs.length - 1),
            { started: previousEvent.started, ended }
          ];
        }

        return [...xs, x];
      }, []);
  }

  getEffectiveTo() {
    return !this.state.to || this.state.to > new Date()
      ? new Date()
      : this.state.to;
  }

  render() {
    const windows = ["D", "W", "M", "Y"];

    if (this.props.customWindows) {
      this.props.customWindows.forEach(w => windows.push(w));
    }

    const isCustomWindow = !isKnownWindow(this.state.window);

    const maxEndDate = this.props.maxEndDate.date || moment().toDate();

    const nextRangeEndsAfterMaxEndDate =
      isKnownWindow(this.state.window) &&
      isDateAfterEndDate(this.getNextRange().to, maxEndDate);

    const canGoForward = !isCustomWindow && !nextRangeEndsAfterMaxEndDate;
    const canGoBackward = !isCustomWindow;

    const gotData = !!this.state.values && !!this.state.alertEvents;

    const actualsSeries = gotData
      ? this.createActualsSeries()
      : this.createEmptySeries("actual", "actual");
    const baselineSeries = gotData
      ? this.createBaselineSeries()
      : this.createEmptySeries("baseline", "baseline");
    const actualAlertOverlaySeries =
      gotData && this.props.hasAlerts
        ? this.createActualAlertOverlaySeries()
        : this.createEmptySeries("alertActual", "alertActual");
    const alertSeries =
      gotData && this.props.hasAlerts
        ? this.createAlertSeries()
        : this.createEmptySeries("alertActual", "zeroAlertMarker");
    const lightsOverThresholdAlertSeries = gotData
      ? this.createLightsOverThresholdAlertSeries()
      : this.createEmptySeries(
          "lightsOverThresholdAlertThreshold",
          "lightsOverThresholdAlertThreshold"
        );
    const hvacOverThresholdAlertSeries = gotData
      ? this.createHvacOverThresholdAlertSeries()
      : this.createEmptySeries(
          "hvacOverThresholdAlertThreshold",
          "hvacOverThresholdAlertThreshold"
        );

    let showLightsOverThresholdAlertThresholdOnGraph =
      this.state.showStoreClosedOverlay && this.state.showStoreClosedCheckbox;
    let showLightsOverThresholdAlertThresholdInFooter = this.state
      .showStoreClosedCheckbox;
    if (!this.props.lightsOverThresholdAlertThresholdsEnabled) {
      showLightsOverThresholdAlertThresholdOnGraph = false;
      showLightsOverThresholdAlertThresholdInFooter = false;
    }

    let showHvacOverThresholdAlertThresholdOnGraph =
      this.state.showStoreClosedOverlay && this.state.showStoreClosedCheckbox;
    let showHvacOverThresholdAlertThresholdInFooter = this.state
      .showStoreClosedCheckbox;
    if (!this.props.hvacOverThresholdAlertThresholdsEnabled) {
      showHvacOverThresholdAlertThresholdOnGraph = false;
      showHvacOverThresholdAlertThresholdInFooter = false;
    }

    return (
      <div className="cb-c-energy-chart">
        {this.props.title}
        {this.props.showHeader && (
          <EnergyChartHeader
            from={this.state.from}
            to={this.state.to}
            windows={windows}
            selectedWindow={this.state.window}
            onWindowChange={this.handleWindowChange}
            canGoBackward={canGoBackward}
            canGoForward={canGoForward}
            onBackwardClick={this.handleBackwardClick}
            onDateChange={this.handleDateChange}
            onForwardClick={this.handleForwardClick}
            onGoToMaxEndDateClick={this.handleGoToMaxEndDateClick}
            goToMaxEndDateButtonText={this.props.maxEndDate.buttonText}
          />
        )}
        <EnergyChartGraph
          sensor={this.state.sensor}
          from={this.state.loadedDataFrom || this.state.from}
          to={this.state.loadedDataTo || this.state.to}
          effectiveTo={this.getEffectiveTo()}
          values={this.state.values}
          chartHeight={this.props.chartHeight}
          showSeriesLabel={this.props.showSeriesLabel}
          isLoading={this.state.isLoading}
          loadingFailed={this.state.loadingFailed}
          onTimeRangeChanged={this.handleTimeRangeChanged}
          closedTimes={this.state.closedTimes}
          showStoreClosedCheckbox={this.state.showStoreClosedCheckbox}
          showStoreClosedOverlay={this.state.showStoreClosedOverlay}
          actualsSeries={actualsSeries}
          baselineSeries={baselineSeries}
          actualAlertOverlaySeries={actualAlertOverlaySeries}
          alertSeries={alertSeries}
          lightsOverThresholdAlertSeries={lightsOverThresholdAlertSeries}
          showLightsOverThresholdAlertThreshold={
            showLightsOverThresholdAlertThresholdOnGraph
          }
          hvacOverThresholdAlertSeries={hvacOverThresholdAlertSeries}
          showHvacOverThresholdAlertThreshold={
            showHvacOverThresholdAlertThresholdOnGraph
          }
          useZeroMinYValue={this.props.useZeroMinYValue}
        />
        {this.props.showFooter && (
          <EnergyChartFooter
            showStoreClosedCheckbox={this.state.showStoreClosedCheckbox}
            showStoreClosedOverlay={this.state.showStoreClosedOverlay}
            callbackFromParent={this.handleStoreClosedChanged}
            showLightsOverThresholdAlertThreshold={
              showLightsOverThresholdAlertThresholdInFooter
            }
            showHvacOverThresholdAlertThreshold={
              showHvacOverThresholdAlertThresholdInFooter
            }
            hasBaseline={this.props.hasBaseline}
            actualsSeriesLabel={this.props.actualsSeriesLabel}
          />
        )}
      </div>
    );
  }

  getResolutionFromDateRange(from, to) {
    const duration = moment.duration(moment(to).diff(moment(from)));
    const days = duration.asDays();
    const months = duration.asMonths();

    if (days <= 1) {
      return this.props.sensorDataResolution.D;
    }

    if (days < 28) {
      return this.props.sensorDataResolution.W;
    }

    if (months < 2) {
      return this.props.sensorDataResolution.M;
    }

    return this.props.sensorDataResolution.Y;
  }

  getNextRange = () =>
    getNextWindow(
      { from: this.state.from, to: this.state.to },
      this.state.window
    );

  getPreviousRange = () =>
    getPreviousWindow(
      { from: this.state.from, to: this.state.to },
      this.state.window
    );

  handleBackwardClick = () => {
    const { from, to } = this.getPreviousRange();

    this.setState({
      from,
      to
    });
    this.loadData(from, to);
  };

  handleDateChange = (startDate, endDate) => {
    const { from, to } = {
      from: startDate.startOf("day").toDate(),
      to: endDate.endOf("day").toDate()
    };
    const window = "";
    this.setState({
      from,
      to,
      window
    });
    this.loadData(from, to);
  };

  handleForwardClick = () => {
    const { from, to } = this.getNextRange();

    this.setState({
      from,
      to
    });
    this.loadData(from, to);
  };

  handleLiveFeedData = data => {
    const mergedData = [
      ...this.state.values,
      [data.time, data.value, data.baseline]
    ].sort((a, b) => new Date(a[0]) - new Date(b[0]));

    this.setState({ values: mergedData });
  };

  handleTimeRangeChanged = range => {
    const from = range.begin();
    const to = range.end();

    this.setState({
      from,
      to,
      window: null
    });

    this.loadData(from, to);
  };

  handleGoToMaxEndDateClick = () => {
    const window = isKnownWindow(this.state.window) ? this.state.window : "D";

    const { from, to } = getDateRangeFromDateAndWindow(
      this.props.maxEndDate.date || new Date(),
      window
    );
    this.setState({
      window,
      from,
      to
    });
    this.loadData(from, to);
  };

  handleWindowChange = w => {
    const { from, to } = isKnownWindow(w)
      ? getDateRangeFromDateAndWindow(this.state.to, w)
      : this.props.getDateRangeForWindow(w);

    this.setState({
      window: w,
      from,
      to
    });
    this.loadData(from, to);
  };

  loadStoreClosedTimes(from, to) {
    this.props
      .loadStoreOpeningHours(this.props.storeId, from, to)
      .then(response => {
        const openingHours = response.openingHours;
        const closedTimes = [];
        const format = dateTimeFormat;

        const firstClosesAt = moment(
          `${openingHours[0].date} 00:00:00`,
          format
        );
        const firstOpensAt = moment(
          `${openingHours[0].date} ${openingHours[0].opens}`,
          format
        );
        const firstClosure = new TimeRange(firstClosesAt, firstOpensAt);
        closedTimes.push(firstClosure);

        openingHours.forEach((opening, index) => {
          let closes = opening.closes;
          if (opening.closes === "00:00:00" && opening.opens !== "00:00:00") {
            closes = "23:59:59";
          }
          const closesAt = moment(opening.date + " " + closes, format);

          let opensAt;
          if (openingHours.length > index + 1) {
            opensAt = moment(
              `${openingHours[index + 1].date} ${
                openingHours[index + 1].opens
              }`,
              format
            );
          } else {
            opensAt = moment(`${opening.date} 00:00:00`, format).add(1, "days");
          }

          const closure = new TimeRange(closesAt, opensAt);
          closedTimes.push(closure);
        });

        this.setState({ closedTimes: closedTimes });
      });
  }

  loadLightsOverThresholdAlertThresholds(from, to) {
    this.props
      .loadLightsOverThresholdAlertThresholds(this.props.sensorId, from, to)
      .then(response => {
        const thresholds = response.thresholds.map(threshold => {
          return {
            time: threshold.time,
            value: threshold.value
          };
        });

        this.setState({ lightsOverThresholdAlertThresholds: thresholds });
      });
  }

  loadHvacOverThresholdAlertThresholds(from, to) {
    this.props
      .loadHvacOverThresholdAlertThresholds(this.props.sensorId, from, to)
      .then(response => {
        const thresholds = response.thresholds.map(threshold => {
          return {
            time: threshold.time,
            value: threshold.value
          };
        });

        this.setState({ hvacOverThresholdAlertThresholds: thresholds });
      });
  }

  loadAlertEvents(from, to) {
    this.props.loadAlertEvents(this.props.sensorId, from, to).then(response => {
      const alertEvents = response.alertEvents ? response.alertEvents : [];

      this.setState({ alertEvents: alertEvents });
    });
  }

  handleStoreClosedChanged = checked => {
    this.setState({ showStoreClosedOverlay: checked });
  };

  async loadData(from, to) {
    const dataResolution =
      this.props.dataResolution || this.getResolutionFromDateRange(from, to);
    const differenceInDays = moment(to).diff(moment(from), "days");
    const showStoreClosedCheckbox = differenceInDays <= 7;
    const getDataPromises = [];
    const sensorId = this.props.sensorDataSensorId
      ? this.props.sensorDataSensorId
      : this.props.sensorId;

    const newRangeIsStrictSubsetOfCurrentRange =
      this.state.cachedDataSensorId === sensorId &&
      this.state.cachedDataFrom <= from &&
      this.state.cachedDataTo >= to;

    // Get the store's closed times.
    // We can avoid a call here if we already have the data from the previous call.

    if (
      showStoreClosedCheckbox &&
      (!newRangeIsStrictSubsetOfCurrentRange ||
        !this.state.cachedDataHasOpeningHours)
    ) {
      getDataPromises.push(this.loadStoreClosedTimes(from, to));

      if (this.props.lightsOverThresholdAlertThresholdsEnabled) {
        getDataPromises.push(
          this.loadLightsOverThresholdAlertThresholds(from, to)
        );
      }

      if (this.props.hvacOverThresholdAlertThresholdsEnabled) {
        getDataPromises.push(
          this.loadHvacOverThresholdAlertThresholds(from, to)
        );
      }
    }

    if (this.props.hasAlerts) {
      getDataPromises.push(this.loadAlertEvents(from, to));
    }

    if (
      !(
        newRangeIsStrictSubsetOfCurrentRange &&
        this.state.cachedDataResolution === dataResolution
      )
    ) {
      // Get data for range
      getDataPromises.push(
        this.props
          .loadSensorData(
            sensorId,
            dataResolution,
            from,
            to,
            this.props.convertToNonCumulative
          )
          .then(async data => {
            this.setState({
              sensor: this.props.sensor ? this.props.sensor : data.sensor,
              values: data.data.values,
              cachedDataSensorId: sensorId,
              cachedDataFrom: from,
              cachedDataTo: to,
              cachedDataResolution: dataResolution,
              cachedDataHasOpeningHours: showStoreClosedCheckbox
            });

            if (!this.props.hasAlerts) {
              this.setState({ alertEvents: [] });
            }
          })
      );
    }

    if (getDataPromises.length) {
      this.setState({
        isLoading: true,
        loadingFailed: false
      });
    }

    try {
      await Promise.all(getDataPromises);

      // N.B. We set loadedData{To|From} here so the graph doesn't shift its time range before the data has loaded.
      this.setState({
        isLoading: false,
        loadingFailed: false,
        loadedDataFrom: from,
        loadedDataTo: to,
        showStoreClosedCheckbox: showStoreClosedCheckbox
      });
    } catch (e) {
      this.setState({
        isLoading: false,
        loadingFailed: true
      });
    }
  }
}

EnergyChart.propTypes = {
  loadSensorData: PropTypes.func.isRequired,
  loadStoreOpeningHours: PropTypes.func.isRequired,
  sensorId: PropTypes.string.isRequired,
  storeId: PropTypes.string.isRequired,
  date: PropTypes.instanceOf(Date),
  showHeaders: PropTypes.bool,
  showFooter: PropTypes.bool,
  showSeriesLabel: PropTypes.bool,
  window: PropTypes.string,
  customWindows: PropTypes.array,
  getDateRangeForWindow: PropTypes.func,
  chartHeight: PropTypes.number,
  lightsOverThresholdAlertThresholdsEnabled: PropTypes.bool,
  loadLightsOverThresholdAlertThresholds: PropTypes.func,
  hvacOverThresholdAlertThresholdsEnabled: PropTypes.bool,
  loadHvacOverThresholdAlertThresholds: PropTypes.func,
  title: PropTypes.object,
  dataResolution: PropTypes.string,
  hasBaseline: PropTypes.bool,
  actualsSeriesLabel: PropTypes.string.isRequired,
  hasAlerts: PropTypes.bool,
  maxEndDate: PropTypes.shape({
    date: PropTypes.instanceOf(Date),
    buttonText: PropTypes.string.isRequired
  }).isRequired,
  useZeroMinYValue: PropTypes.bool,
  sensorDataSensorId: PropTypes.string,
  loadAlertEvents: PropTypes.func
};

EnergyChart.defaultProps = {
  showHeader: true,
  showFooter: true,
  showSeriesLabel: true,
  window: "D",
  chartHeight: 200,
  hasBaseline: true,
  actualsSeriesLabel: "Actuals",
  maxEndDate: {
    buttonText: "Show Today"
  },
  sensorDataResolution: {
    D: "RealTime",
    W: "FifteenMinutes",
    M: "Daily",
    Y: "Daily"
  }
};

export default EnergyChart;
